import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Layers, Loader2, Search } from "lucide-react";
import { type FeatureLifecycle } from "@/lib/feature-catalog";
import {
  FM_OPERATIONAL_DOMAINS,
  type FeatureAdminMetaDbPatch,
  type FeatureFilterId,
  type FeatureType,
  type OperationalDomain,
  getEnrichedFeatures,
  searchEnrichedFeatures,
} from "@/lib/feature-catalog-admin";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

type Lifecycle = FeatureLifecycle;
interface Override { enabled?: boolean; lifecycle?: Lifecycle }

const LIFECYCLE_META: Record<Lifecycle, { label: string; badge: string }> = {
  stable: { label: "Estable", badge: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  beta: { label: "Beta", badge: "bg-sky-500/15 text-sky-400 border-sky-500/30" },
  upcoming: { label: "En progreso", badge: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  deprecated: { label: "Deprecado", badge: "bg-rose-500/15 text-rose-400 border-rose-500/30" } };
const PLAN_LABEL: Record<string, string> = {
  starter: "Starter",
  pro: "Pro",
  business: "Business",
  enterprise: "Enterprise",
  all: "Todos",
};
const LIFECYCLES: Lifecycle[] = ["stable", "beta", "upcoming", "deprecated"];
const PLAN_FILTERS = ["all", "starter", "pro", "business", "enterprise"] as const;

const TYPE_FILTERS: { id: FeatureFilterId; label: string }[] = [
  { id: "core", label: "Core" },
  { id: "integrations", label: "Integraciones" },
  { id: "addons", label: "Addons" },
  { id: "beta", label: "Beta" },
  { id: "experimental", label: "Experimental" },
  { id: "hidden_marketing", label: "Ocultas" },
  { id: "enterprise", label: "Enterprise" },
];

const DOMAIN_OPTIONS = FM_OPERATIONAL_DOMAINS.filter((d) => d.id !== "legacy").map((d) => d.id);
const TYPE_OPTIONS: FeatureType[] = [
  "core",
  "addon",
  "integration",
  "experimental",
  "enterprise",
  "deprecated",
];

export function FeatureCatalogPanel() {
  const [overrides, setOverrides] = useState<Record<string, Override>>({});
  const [metaOverrides, setMetaOverrides] = useState<Record<string, FeatureAdminMetaDbPatch>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [editingMeta, setEditingMeta] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [lifecycleFilter, setLifecycleFilter] = useState<Lifecycle | "all">("all");
  const [planFilter, setPlanFilter] = useState<(typeof PLAN_FILTERS)[number]>("all");
  const [typeFilters, setTypeFilters] = useState<Set<FeatureFilterId>>(new Set());
  const [openDomains, setOpenDomains] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(FM_OPERATIONAL_DOMAINS.map((d) => [d.id, d.defaultOpen])),
  );

  useEffect(() => {
    void (async () => {
      try {
        const [flags, meta] = await Promise.all([
          apiGetJson<{ overrides?: Record<string, Override> }>("/api/admin/feature-flags"),
          apiGetJson<{ items?: Record<string, FeatureAdminMetaDbPatch> }>("/api/admin/feature-metadata"),
        ]);
        setOverrides(flags.overrides ?? {});
        setMetaOverrides(meta.items ?? {});
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const patchFeature = useCallback(async (id: string, patch: Override) => {
    setSaving(id);
    setOverrides((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    try {
      await apiSendJson("/api/admin/feature-flags", "PATCH", { id, ...patch });
      toast.success("Feature actualizado");
    } catch {
      toast.error("No se pudo guardar el cambio");
    } finally {
      setSaving(null);
    }
  }, []);

  const patchMeta = useCallback(async (id: string, patch: FeatureAdminMetaDbPatch) => {
    setSaving(id);
    setMetaOverrides((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    try {
      const d = await apiSendJson<{ items?: Record<string, FeatureAdminMetaDbPatch> }>(
        "/api/admin/feature-metadata",
        "PATCH",
        { id, ...patch },
      );
      setMetaOverrides(d.items ?? {});
      toast.success("Metadata guardada");
      setEditingMeta(null);
    } catch {
      toast.error("No se pudo guardar metadata");
    } finally {
      setSaving(null);
    }
  }, []);

  const rows = useMemo(() => {
    const base = searchEnrichedFeatures(query, typeFilters, false, metaOverrides);
    return base
      .filter((f) => lifecycleFilter === "all" || f.lifecycle === lifecycleFilter)
      .filter((f) => planFilter === "all" || f.planTier === planFilter || f.planTier === "all")
      .map((f) => {
        const ov = overrides[f.id] ?? {};
        return {
          ...f,
          effLifecycle: (ov.lifecycle ?? f.lifecycle) as Lifecycle,
          effEnabled: ov.enabled ?? f.lifecycle !== "deprecated",
        };
      });
  }, [overrides, query, lifecycleFilter, planFilter, typeFilters, metaOverrides]);

  const filtered = rows;

  const byDomain = useMemo(() => {
    const map = new Map<string, typeof filtered>();
    for (const d of FM_OPERATIONAL_DOMAINS) {
      if (d.id === "legacy") continue;
      map.set(
        d.id,
        filtered.filter((f) => f.admin.operationalDomain === d.id),
      );
    }
    return map;
  }, [filtered]);

  if (loading) {
    return (
      <div className="glass-card flex items-center gap-2 rounded-xl p-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando catálogo de features…
      </div>
    );
  }

  return (
    <div className="glass-card rounded-2xl p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-primary" />
          <h2 className="text-base font-semibold text-foreground">Catálogo de features</h2>
          <span className="text-xs text-muted-foreground">
            {filtered.length} / {getEnrichedFeatures(metaOverrides).length}
          </span>
        </div>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        Overrides globales por dominio operacional. Integraciones = providers (no core). No altera runtime hasta guardar.
      </p>

      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar feature…"
            className="h-9 pl-8 text-sm"
          />
        </div>
        <select
          value={lifecycleFilter}
          onChange={(e) => setLifecycleFilter(e.target.value as Lifecycle | "all")}
          className="h-9 rounded-md border border-border bg-background px-2 text-sm"
        >
          <option value="all">Todos los estados</option>
          {LIFECYCLES.map((lc) => (
            <option key={lc} value={lc}>{LIFECYCLE_META[lc].label}</option>
          ))}
        </select>
        <select
          value={planFilter}
          onChange={(e) => setPlanFilter(e.target.value as (typeof PLAN_FILTERS)[number])}
          className="h-9 rounded-md border border-border bg-background px-2 text-sm"
        >
          <option value="all">Todos los planes</option>
          {PLAN_FILTERS.filter((p) => p !== "all").map((p) => (
            <option key={p} value={p}>{PLAN_LABEL[p]}</option>
          ))}
        </select>
        <div className="flex flex-wrap gap-1">
          {TYPE_FILTERS.map((tf) => (
            <button
              key={tf.id}
              type="button"
              className={`rounded-md border px-2 py-1 text-[11px] ${
                typeFilters.has(tf.id)
                  ? "border-primary/40 bg-primary/10"
                  : "border-border text-muted-foreground"
              }`}
              onClick={() =>
                setTypeFilters((prev) => {
                  const next = new Set(prev);
                  if (next.has(tf.id)) next.delete(tf.id);
                  else next.add(tf.id);
                  return next;
                })
              }
            >
              {tf.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-h-[min(70vh,640px)] space-y-2 overflow-auto">
        {FM_OPERATIONAL_DOMAINS.filter((d) => d.id !== "legacy").map((domain) => {
          const domainRows = byDomain.get(domain.id) ?? [];
          if (!domainRows.length && (query || typeFilters.size)) return null;
          const open = openDomains[domain.id] ?? domain.defaultOpen;
          return (
            <div key={domain.id} className="rounded-lg border border-border/60">
              <button
                type="button"
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium"
                onClick={() => setOpenDomains((p) => ({ ...p, [domain.id]: !open }))}
              >
                <span>
                  {domain.name}
                  <span className="ml-2 text-xs font-normal text-muted-foreground">{domainRows.length}</span>
                </span>
                <span className="text-xs text-muted-foreground">{open ? "▾" : "▸"}</span>
              </button>
              {open && (
                <table className="w-full border-t border-border/60 text-sm">
                  <tbody>
                    {domainRows.map((f) => (
                      <Fragment key={f.id}>
                      <tr className="border-b border-border/40 last:border-0 hover:bg-secondary/20">
                        <td className="px-3 py-2">
                          <p className="font-medium text-foreground">{f.name}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {f.id} · {f.admin.featureType}
                            {f.admin.requires?.length ? ` · req: ${f.admin.requires.join(", ")}` : ""}
                          </p>
                        </td>
                        <td className="hidden px-3 py-2 md:table-cell">
                          <span className="rounded border border-border/60 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                            {PLAN_LABEL[f.planTier] ?? f.planTier}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={f.effLifecycle}
                            onChange={(e) => void patchFeature(f.id, { lifecycle: e.target.value as Lifecycle })}
                            className={`rounded-md border px-2 py-1 text-xs font-medium ${LIFECYCLE_META[f.effLifecycle].badge}`}
                          >
                            {LIFECYCLES.map((lc) => (
                              <option key={lc} value={lc} className="bg-background text-foreground">
                                {LIFECYCLE_META[lc].label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              className="text-[10px] text-muted-foreground hover:text-primary"
                              onClick={() => setEditingMeta(editingMeta === f.id ? null : f.id)}
                            >
                              Meta
                            </button>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={f.effEnabled}
                              onClick={() => void patchFeature(f.id, { enabled: !f.effEnabled })}
                              className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
                                f.effEnabled ? "bg-primary" : "bg-muted"
                              }`}
                            >
                              <span
                                className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                                  f.effEnabled ? "translate-x-[22px]" : "translate-x-0.5"
                                }`}
                              />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {editingMeta === f.id && (
                        <tr key={`${f.id}-meta`} className="border-b border-border/40 bg-muted/10">
                          <td colSpan={4} className="px-3 py-3">
                            <div className="flex flex-wrap items-end gap-2 text-xs">
                              <label className="flex flex-col gap-1">
                                Dominio
                                <select
                                  className="rounded border border-border bg-background px-2 py-1"
                                  value={f.admin.operationalDomain}
                                  onChange={(e) =>
                                    void patchMeta(f.id, {
                                      operationalDomain: e.target.value as OperationalDomain,
                                    })
                                  }
                                >
                                  {DOMAIN_OPTIONS.map((d) => (
                                    <option key={d} value={d}>{d}</option>
                                  ))}
                                </select>
                              </label>
                              <label className="flex flex-col gap-1">
                                Tipo
                                <select
                                  className="rounded border border-border bg-background px-2 py-1"
                                  value={f.admin.featureType}
                                  onChange={(e) =>
                                    void patchMeta(f.id, { featureType: e.target.value as FeatureType })
                                  }
                                >
                                  {TYPE_OPTIONS.map((t) => (
                                    <option key={t} value={t}>{t}</option>
                                  ))}
                                </select>
                              </label>
                              <label className="flex min-w-[200px] flex-1 flex-col gap-1">
                                Aliases (coma)
                                <Input
                                  className="h-8 text-xs"
                                  defaultValue={(f.admin.aliases ?? []).join(", ")}
                                  onBlur={(e) => {
                                    const aliases = e.target.value
                                      .split(",")
                                      .map((s) => s.trim())
                                      .filter(Boolean);
                                    void patchMeta(f.id, { aliases });
                                  }}
                                />
                              </label>
                              <label className="flex items-center gap-2 pb-1">
                                <input
                                  type="checkbox"
                                  checked={f.admin.upgradeDriver === true}
                                  onChange={(e) =>
                                    void patchMeta(f.id, { upgradeDriver: e.target.checked })
                                  }
                                />
                                Upgrade driver
                              </label>
                              {saving === f.id && (
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">Sin resultados para los filtros actuales.</p>
        )}
      </div>
    </div>
  );
}
